import { useMemo } from "react";
import { Accordion, Flex, QueryLoader, Tag, Text } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { DocumentStatus, TaxDataSourceAssignmentStatus } from "~/graphql/supergraphEnums";
import {
  useGetPositionsForCaseQuery,
  useGetTaxDataSourceAssignmentsQuery,
  useGetTaxReportingDataQuery,
} from "~/graphql/supergraphHooks";
import { type GetProfileDetailsQuery } from "~/graphql/supergraphTypes";
import { POSITION_CASE_TYPES } from "~/pages/Compliance/CaseDetailsDrawer/Documents/DocumentManagement/DocumentDetailsRightSidebar/DocumentStatusCard/FatcaAssignmentModal/constants";
import { TaxDetailsTable } from "~/pages/Compliance/CaseDetailsDrawer/Documents/DocumentManagement/DocumentDetailsRightSidebar/TaxDetailsCard/TaxDetailsTable";
import {
  getTaxDetailsFromMetadata,
  type ProfileFieldsData,
} from "~/pages/Compliance/CaseDetailsDrawer/Documents/DocumentManagement/DocumentDetailsRightSidebar/TaxDetailsCard/utils";
import { isDocumentExpiredInList } from "~/pages/Compliance/CaseDetailsDrawer/Documents/DocumentManagement/DocumentManagementList/utils";
import {
  getCategoryLabel,
  getDocumentCategory,
  getFormTypeLabel,
  getSecondaryDocumentType,
  getTaxFormTypeFromDocument,
} from "~/pages/Compliance/CaseDetailsDrawer/Documents/TaxForms";
import { getInvestingProfileName } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/utils";
import { toStandardFormat } from "~/utils/datetime";
import { toDbId } from "~/utils/graphql-helpers";
import * as styles from "./styles_vanilla.css";
import { getDocumentStatus } from "./utils";

type ProfileDocument = NonNullable<
  GetProfileDetailsQuery["complianceProfile"]
>["documents"][number];

export type TaxDocumentAccordionProps = {
  document: ProfileDocument;
  caseId: number;
  profileId: number;
  countryCodeToName: Record<string, string>;
  scopeToCurrentCase?: boolean;
};

export const TaxDocumentAccordion = ({
  document,
  caseId,
  profileId,
  countryCodeToName,
  scopeToCurrentCase = false,
}: TaxDocumentAccordionProps) => {
  const category = getDocumentCategory(document.documentPurpose?.baseType);
  const secondaryDocumentType = getSecondaryDocumentType(document.metadata);
  const taxFormType = getTaxFormTypeFromDocument(category, secondaryDocumentType);
  const formTypeLabel = getFormTypeLabel(category, secondaryDocumentType);
  const categoryLabel = getCategoryLabel(category);
  const status = getDocumentStatus(document.status);
  const expired = isDocumentExpiredInList(document);

  const taxReportingResult = useGetTaxReportingDataQuery({
    variables: { caseId, documentId: document.id },
    skip: !caseId || caseId <= 0 || !document.id,
    fetchPolicy: "cache-and-network",
  });

  const assignmentsResult = useGetTaxDataSourceAssignmentsQuery({
    variables: { input: { documentId: document.id } },
    skip: !document.id,
    fetchPolicy: "cache-and-network",
  });

  const positionsResult = useGetPositionsForCaseQuery({
    variables: { profileId, caseTypes: [...POSITION_CASE_TYPES] },
    skip: !profileId,
    fetchPolicy: "cache-and-network",
  });

  const taxReportingData = taxReportingResult.data;
  const assignmentsData = assignmentsResult.data;
  const positionsData = positionsResult.data;

  // Under IO reusable profiles a document can carry assignments from other onboarding cases.
  // Such a document belongs to those cases only, so it isn't surfaced on this case at all.
  const isHiddenForCurrentCase =
    scopeToCurrentCase &&
    !(assignmentsData?.taxDataSourceAssignments ?? []).some(
      (a) =>
        a.status === TaxDataSourceAssignmentStatus.Assigned && a.referencedCaseId === caseId,
    );

  const sourceDocumentAssignments = useMemo(() => {
    const assignments = (assignmentsData?.taxDataSourceAssignments ?? []).filter(
      (a) => a.status === TaxDataSourceAssignmentStatus.Assigned,
    );
    const referencedCaseIds = new Set(
      assignments
        .map((a) => a.referencedCaseId)
        .filter((id): id is number => typeof id === "number" && id > 0),
    );
    const fallbackArenaDbId = positionsData?.complianceProfile?.clients?.[0];

    const byCaseId = new Map<
      number,
      {
        investorName: string;
        entityName?: string;
        positionId?: string;
        arenaId?: number;
      }
    >();

    for (const p of positionsData?.complianceProfile?.positions ?? []) {
      const oc = p.onboardingCase;
      if (!oc || !referencedCaseIds.has(oc.id)) continue;
      const arenaRelayId = (p.arena as { id: string } | null | undefined)?.id;
      byCaseId.set(oc.id, {
        investorName: getInvestingProfileName(oc.investingProfile) ?? "",
        entityName: oc.externalEntity?.name ?? undefined,
        positionId: p.id ?? undefined,
        arenaId: arenaRelayId ? toDbId(arenaRelayId) : fallbackArenaDbId,
      });
    }

    // Fallback for IO cases where the profile may not yet have a position
    // linked to the onboarding case — surface the assignment via complianceCases.
    for (const c of positionsData?.complianceProfile?.complianceCases ?? []) {
      const cc = c.complianceCase;
      if (!cc || !referencedCaseIds.has(cc.id) || byCaseId.has(cc.id)) continue;
      byCaseId.set(cc.id, {
        investorName: getInvestingProfileName(cc.investingProfile) ?? "",
        entityName: cc.externalEntity?.name ?? undefined,
        positionId: cc.position?.id ?? undefined,
        arenaId: cc.externalArena?.id ?? fallbackArenaDbId,
      });
    }

    return Array.from(byCaseId.values());
  }, [assignmentsData, positionsData]);

  const signatureDate = useMemo(() => {
    const dateValue = document.metadata?.find((m) => m.key === "date")?.value;
    return dateValue ? toStandardFormat(dateValue) : undefined;
  }, [document.metadata]);

  const profileFieldsData: ProfileFieldsData = useMemo(
    () => ({
      signatureDate,
      documentName: document.name ?? undefined,
      sourceDocumentAssignments,
      isExpired: expired,
    }),
    [signatureDate, document.name, sourceDocumentAssignments, expired],
  );

  const { taxDetails, hasDetails } = useMemo(() => {
    if (!taxFormType) {
      return { taxDetails: [], hasDetails: false };
    }
    const details = getTaxDetailsFromMetadata(
      document.metadata ?? null,
      taxFormType,
      countryCodeToName,
      taxReportingData?.taxReportingData ?? [],
      { includeProfileFields: true, profileFieldsData },
    );
    return {
      taxDetails: details,
      hasDetails: details.some((detail) => detail.hasValue && !detail.isSectionHeader),
    };
  }, [document.metadata, taxFormType, countryCodeToName, taxReportingData, profileFieldsData]);

  const getStatusDisplay = () => {
    if (status === DocumentStatus.Verified) {
      return { variant: "success" as const, text: "Verified" };
    }
    if (status === DocumentStatus.Invalid) {
      return { variant: "error" as const, text: "Not valid" };
    }
    return { variant: undefined, text: undefined };
  };

  const { variant: statusVariant, text: statusText } = getStatusDisplay();

  // Hide accordion if no assignments exist for this document
  if (sourceDocumentAssignments.length === 0 || isHiddenForCurrentCase) {
    return null;
  }

  const title = (
    <Flex flexDirection="column" gap={Sizes.XS} className={styles.titleRow}>
      <Flex alignItems="center" gap={Sizes.SM}>
        <Text variant="strong-md">{formTypeLabel}</Text>
        {expired && (
          <Tag variant="warning" label="Expired" className={styles.expiredTagWrapper} />
        )}
      </Flex>
      <Text variant="secondary-sm">{categoryLabel}</Text>
    </Flex>
  );

  return (
    <div className={styles.accordionWrapper}>
      <Accordion
        title={title}
        statusText={statusText}
        statusVariant={statusVariant}
        disableBodyPadding
      >
        <QueryLoader result={[taxReportingResult, assignmentsResult, positionsResult]}>
          {hasDetails ? (
            <div className={styles.tableContainer}>
              <TaxDetailsTable taxDetails={taxDetails} />
            </div>
          ) : (
            <Text variant="regular-md" className={styles.noDetailsText}>
              No tax details available
            </Text>
          )}
        </QueryLoader>
      </Accordion>
    </div>
  );
};
